import { Cylinder, Edges } from '@react-three/drei'
import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { AssetVizMode } from '@asset-viz/defines'
import { useSurfaceProps } from '@asset-viz/objects/surfaces/util/SurfaceProvider'
import { Vec3 } from '@asset-viz/utils/helpers'
import { RenderLayerID } from '@asset-viz/utils/renderingDefines'
import React from 'react'
import { Color } from 'three'
import { GeckoFeature, GeckoFixedSensor, GeckoNozzle } from 'types'

/**
 * A cylinder within a feature's procedural geometry
 * @param feature       The feature this cylinder belong to
 * @param position      The position of the center of the base of this cylinder
 * @param radius        The radius of this cylinder
 * @param height        The height of this cylinder
 * @param debugColor    A color used to identify this cylinder when debugging
 *                      features' procedural geometry
 * @param thetaStart    Optional. Start in radians of the circular sector.
 *                      Default is 0.
 * @param thetaEnd      Optional. End in radians of the circular sector.
 *                      Default is 2 * Math.PI.
 * @constructor
 */
export const FeatureCylinder3D: React.FC<{
  feature: GeckoFeature
  position: Vec3
  radius: number
  height: number
  debugColor: string
  color?: string
  thetaStart?: number
  thetaEnd?: number
}> = ({
  feature,
  position,
  radius,
  height,
  debugColor,
  color,
  thetaStart = 0,
  thetaEnd = 2 * Math.PI,
}) => {
  const { componentModel } = useSurfaceProps()
  const {
    mode,
    showFeatures,
    selection,
    draftSelection,
    assetColorString,
    weldColorString,
    showFeatureDebugColors,
    clipConfig,
    assetOpacity,
  } = useAssetVizInternal()

  const showThisFeature = React.useMemo(
    () =>
      typeof showFeatures === 'boolean'
        ? showFeatures
        : showFeatures({ feature, componentModel }),
    [feature, componentModel, showFeatures]
  )

  const layers = React.useMemo(
    () =>
      !showThisFeature
        ? RenderLayerID.InvisibleNonCollidable
        : feature instanceof GeckoNozzle ||
            feature instanceof GeckoFixedSensor ||
            mode === AssetVizMode.AllFeaturesSelectable
          ? RenderLayerID.VisibleCollidable
          : RenderLayerID.VisibleNonCollidable,
    [feature, showThisFeature, mode]
  )

  const { meshColor, lineWidth, lineColor } = React.useMemo(
    () => ({
      meshColor: new Color(
        showFeatureDebugColors ? debugColor : (color ?? assetColorString)
      ).convertSRGBToLinear(),
      lineWidth: selection === feature || draftSelection === feature ? 4 : 2,
      lineColor: new Color(
        selection === feature || draftSelection === feature
          ? 'white'
          : weldColorString
      ).convertSRGBToLinear(),
    }),
    [
      assetColorString,
      color,
      debugColor,
      draftSelection,
      feature,
      selection,
      showFeatureDebugColors,
      weldColorString,
    ]
  )

  const featureOpacity = React.useMemo(
    () =>
      typeof assetOpacity === 'number'
        ? assetOpacity
        : assetOpacity(componentModel),
    [assetOpacity, componentModel]
  )

  return (
    <Cylinder
      layers={layers}
      userData={{ extrudingFeature: feature }}
      position={[position[0], position[1] + height / 2, position[2]]}
      args={[
        radius, // top radius
        radius, // bottom radius
        height, // height
        undefined, // radial segments
        undefined, // height segments
        undefined, // open ended (bool)
        thetaStart, // theta start
        Math.abs(thetaEnd - thetaStart), // theta length
      ]}>
      <meshBasicMaterial
        attach='material'
        color={meshColor}
        clippingPlanes={clipConfig.planes}
        opacity={featureOpacity}
      />
      <Edges
        layers={layers}
        linewidth={lineWidth}
        color={lineColor}
        clippingPlanes={clipConfig.planes}
      />
    </Cylinder>
  )
}
